import { collection, getDocs } from "firebase/firestore";
import { useEffect, useState } from "react";
import SEO from "../SEO";
import { db } from "../firebase"; // Import Firestore instance
import logo from "../img/Oglitz-main-icon.png";
import "./Design.css";
import DesignHeader from "./DesignHeader";
import DesignWhyChoose from "./DesignWhyChoose";
import PackageCard from "./PackageCard";
import WhatYouGet from "./WhatYouGet";
import DesignProcess from "./DesignProcess";
import DesignFAQ from "./DesignFAQ";
import GlobalCTA from "../components/GlobalCTA";

const DesignPage = () => {
  const [designPackages, setDesignPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "designPackages"));

        const packagesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setDesignPackages(packagesData);
      } catch (error) {
        console.error("Error fetching design packages: ", error);
      } finally {
        setLoading(false); // ✅ ALWAYS stop loading
      }
    };

    fetchPackages();
  }, []);

  const baseUrl = "https://www.oglitz.com";

  const schemaPackages = designPackages.map((pkg) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: pkg.title,
    image: pkg.logo[0], // Assuming the first logo is used as the main image
    description: pkg.description,
    keyFeatures: pkg.keyFeatures,
    benefits: pkg.benefits,
    moreInfo: pkg.moreInfo,
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: pkg.price,
      itemCondition: "https://schema.org/NewCondition",
      url: `${baseUrl}${pkg.link}`,
      availability: "https://schema.org/InStock",
    },
    additionalType: "https://schema.org/Service",
    serviceType: pkg.benefits,
    provider: {
      "@type": "Organization",
      name: "Oglitz Software Pvt Ltd.",
      url: baseUrl,
    },
    additionalInformation: {
      duration: pkg.duration,
      whatsIncluded: pkg.whatsIncluded,
      moreInfo: pkg.moreInfo,
    },
    includedInPackage: pkg.whatsIncluded.map((item) => ({
      "@type": "PropertyValue",
      name: item.feature,
      value: item.detail,
    })),
    demoImages: pkg.logo.map((url) => ({
      "@type": "ImageObject",
      url: url,
    })),
  }));

  return (
    <div className="unique-design-page">
      <SEO
        title="Design Packages | Oglitz Software Pvt Ltd."
        description="Discover the premium design packages offered by Oglitz Software Pvt Ltd., including logo design, branding, and social media assets. Our packages are tailored to meet the needs of businesses looking to enhance their brand identity. Request a quote today to get started!"
        image={logo} // Update with your actual image URL
        url={`${baseUrl}/design`}
        keywords="design packages, logo design, branding, social media design, graphic design, business branding, professional design services, Oglitz Software Pvt Ltd., Oglitz Software"
        author="Oglitz Software Pvt Ltd."
        twitterHandle="@oglitz"
        schemaData={schemaPackages}
      />
      <DesignHeader/>
      <section className="oglitz-packages-section" id="design-packages">
        <div className="oglitz-packages-header">
          <span className="oglitz-packages-badge">Pricing & Packages</span>

          <h2 className="oglitz-packages-title">
            Choose the Right{" "}
            <span className="oglitz-packages-title-accent">
              Design Package{" "}
            </span>
            <br /> for Your Brand
          </h2>

          <p className="oglitz-packages-subtitle">
            Professionally crafted design packages tailored for startups,
            businesses, and growing brands — clear pricing, fast delivery, and
            premium quality.
          </p>
        </div>

        <div className="unique-packages-container">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="oglitz-package-skeleton" />
              ))
            : designPackages.map((pkg) => (
                <PackageCard key={pkg.id} packageData={pkg} />
              ))}
        </div>
      </section>

      <DesignWhyChoose />
      <WhatYouGet />
      <DesignProcess />
      <DesignFAQ />
      <GlobalCTA
        title="Ready to Elevate Your"
        highlight="Brand Design?"
        subtitle="Get high-impact branding, social media creatives, and design systems built for growth."
      />
    </div>
  );
};

export default DesignPage;
